import { Pipe, PipeTransform } from '@angular/core';
import { UserService } from '../../core/user/user.service';

@Pipe({
    name: 'billingDateFormat',
    standalone: false
})
export class BillingDateFormatPipe implements PipeTransform {

    constructor(private user: UserService) { }

    get businessTimezone(): string {
        return this.user.activeBusiness?.timezone;
    }

    /**
     * Formats dates for billing display
     * @param value - Single date string or array of two date strings [startDate, endDate]
     * @param omitYear - Optional flag to omit the year from display
     * @returns Formatted date string
     * 
     * Examples:
     * - Single date with year: "Nov 13, 2025"
     * - Single date without year: "Nov 13"
     * - Date range (same year): "Nov 6 - Nov 13, 2025"
     * - Date range (different years): "Nov 6, 2024 - Nov 13, 2025"
     */
    transform(value: string | [string, string], omitYear: boolean = false): string {
        if (!value) return '';

        const timezone = this.businessTimezone || 'UTC';

        // Handle single date
        if (typeof value === 'string') {
            return this.formatSingleDate(value, timezone, omitYear);
        }

        // Handle date range [startDate, endDate]
        if (Array.isArray(value) && value.length === 2) {
            return this.formatDateRange(value[0], value[1], timezone);
        }

        return '';
    }

    private formatSingleDate(dateString: string, timezone: string, omitYear: boolean): string {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) return 'N/A';

        const options: Intl.DateTimeFormatOptions = {
            timeZone: timezone,
            month: 'short',
            day: 'numeric',
        };

        if (!omitYear) {
            options.year = 'numeric';
        }

        const formatter = new Intl.DateTimeFormat('en-US', options);
        return formatter.format(date);
    }

    private formatDateRange(startDateString: string, endDateString: string, timezone: string): string {
        const startDate = new Date(startDateString);
        const endDate = new Date(endDateString);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 'N/A';

        // Get the year of both dates
        const startYear = this.getYearInTimezone(startDate, timezone);
        const endYear = this.getYearInTimezone(endDate, timezone);

        const isSameYear = startYear === endYear;

        if (isSameYear) {
            // Format: "Nov 6 - Nov 13, 2025"
            const startFormatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                month: 'short',
                day: 'numeric',
            });

            const endFormatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });

            return `${startFormatter.format(startDate)} - ${endFormatter.format(endDate)}`;
        } else {
            // Format: "Nov 6, 2024 - Nov 13, 2025"
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });

            return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
        }
    }

    private getYearInTimezone(date: Date, timezone: string): number {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
        });
        return parseInt(formatter.format(date), 10);
    }
}

